import CanvasChart from './-canvas-chart.ts';
import Button from '../button.gts';
import type { BarHit, BarSelection } from '../../types/charts.ts';
import ChartTooltip from './-chart-tooltip.gts';
import { cached } from '@glimmer/tracking';
import { concat } from '@ember/helper';
import style from 'ember-style-modifier/modifiers/style';
import { setupCanvas } from '../../utils/charts/canvas-utils.ts';
import { linearScale, bandScale, niceAxisTicks } from '../../utils/charts/chart-scales.ts';
import {
  computeLayout,
  formatAxisValue,
  formatNumber,
  topTooltipRows,
} from '../../utils/charts/chart-layout.ts';
import { DEFAULT_CHART_COLORS, getThemeColors } from '../../utils/charts/color-palette.ts';

// HorizontalBarsChart – Canvas-rendered horizontal bars: categories run down
// the Y axis, values along X. Supports stacked (default) and clustered modes.
// Its labels and per-series data shape matches MultiLineChart; ComboChart uses
// the same series shape under its `barSeries` argument. Height is always
// explicit or the container's — never derived from its own content.

/** A single series. `data` is aligned to the parent `labels` array. */
export interface HorizontalBarSeries {
  label: string;
  data: number[];
  /** Bar color (hex). Falls back to the default palette by index when omitted. */
  color?: string;
}

interface HorizontalBarsChartSignature {
  Args: {
    /** Category labels — one row per entry. */
    labels: string[];
    series: HorizontalBarSeries[];
    /** Stacked (default) or clustered side-by-side bars per category. */
    stacked?: boolean;
    /** Fixed height in pixels (default: 300). */
    height?: number;
    /** Formatter for value-axis ticks and tooltip values (default: compact number). */
    formatValue?: (value: number) => string;
    /** Value-axis title (bottom). */
    xLabel?: string;
    /** Category-axis title (left, rotated). */
    yLabel?: string;
    showLegend?: boolean;
    /** Value labels at the end of each bar. */
    showDataLabels?: boolean;
    /**
     * Click on a bar segment, or on a row's empty space (then `seriesIndex` is null).
     * Enables the click listener and the pointer cursor.
     */
    onBarClick?: (hit: BarHit) => void;
    /** Click on a legend entry (`seriesIndex` is into `series`). */
    onLegendClick?: (seriesIndex: number) => void;
    /** Emphasise the matching bars and fade the rest; an omitted field matches every value. */
    selectedBar?: BarSelection;
  };
  Element: HTMLDivElement;
}

const MAX_CATEGORY_LABEL_WIDTH = 170;
const FADED_ALPHA = 0.3;

interface RowRect {
  y: number;
  h: number;
  index: number;
}

interface SegmentRect extends RowRect {
  x: number;
  w: number;
  seriesIndex: number;
}

export default class HorizontalBarsChart extends CanvasChart<HorizontalBarsChartSignature> {
  private rowRects: RowRect[] = [];
  private segmentRects: SegmentRect[] = [];

  get canClick() {
    return Boolean(this.args.onBarClick);
  }

  get canClickLegend() {
    return Boolean(this.args.onLegendClick);
  }

  /** Alpha for a bar segment: hover emphasis, faded when another bar is selected. */
  private segmentAlpha(ci: number, si: number) {
    const sel = this.args.selectedBar;
    if (sel && ((sel.index ?? ci) !== ci || (sel.seriesIndex ?? si) !== si)) return FADED_ALPHA;
    return this.hoveredIndex === ci ? 1 : 0.92;
  }

  get labels() {
    return this.args.labels ?? [];
  }

  get series() {
    return this.args.series ?? [];
  }

  get stacked() {
    return this.args.stacked !== false;
  }

  get showLegend() {
    return this.args.showLegend !== false;
  }

  get showDataLabels() {
    return this.args.showDataLabels === true;
  }

  colorFor(seriesIndex: number) {
    const s = this.series[seriesIndex];
    return s?.color ?? DEFAULT_CHART_COLORS[seriesIndex % DEFAULT_CHART_COLORS.length]!;
  }

  get legendItems() {
    return this.series.map((s, i) => ({
      label: s.label,
      color: this.colorFor(i),
    }));
  }

  get tooltipLabel() {
    if (this.hoveredIndex === null) return '';
    return this.labels[this.hoveredIndex] ?? '';
  }

  @cached
  private get cappedTooltip() {
    if (this.hoveredIndex === null) return { rows: [], moreCount: 0 };
    const idx = this.hoveredIndex;
    const fmt = this.args.formatValue ?? formatNumber;
    return topTooltipRows(
      this.series.map((s, i) => {
        const raw = s.data[idx] ?? 0;
        return { raw, row: { label: s.label, color: this.colorFor(i), value: fmt(raw) } };
      })
    );
  }

  get tooltipRows() {
    return this.cappedTooltip.rows;
  }

  get tooltipMoreCount() {
    return this.cappedTooltip.moreCount;
  }

  get tooltipTotal() {
    if (this.hoveredIndex === null || !this.stacked) return '';
    const idx = this.hoveredIndex;
    const fmt = this.args.formatValue ?? formatNumber;
    return fmt(this.series.reduce((sum, s) => sum + (s.data[idx] ?? 0), 0));
  }

  protected measuresOnNextTick = true;

  trackRedrawInputs() {
    // Touch args so the modifier re-runs when data OR formatting changes.
    void this.labels;
    void this.series;
    void this.stacked;
    void this.args.selectedBar;
    // Touch the height ARG (not the getter — the getter reads containerHeight,
    // which the resize observer below writes, which would loop the modifier).
    void this.args.height;
    void this.args.formatValue;
    void this.args.xLabel;
    void this.args.yLabel;
    void this.showLegend;
    void this.showDataLabels;
  }

  draw(canvas: HTMLCanvasElement) {
    const width = this.containerWidth;
    const height = this.height;
    const labels = this.labels;
    const series = this.series;
    if (!labels.length || !series.length) return;

    const theme = getThemeColors();
    const ctx = setupCanvas(canvas, width, height);
    ctx.clearRect(0, 0, width, height);
    ctx.font = '11px system-ui, sans-serif';

    // Left margin fits the longest (possibly truncated) category label.
    const maxLabelWidth = Math.min(
      MAX_CATEGORY_LABEL_WIDTH,
      Math.max(...labels.map((l) => ctx.measureText(l).width), 30)
    );
    const left = Math.ceil(maxLabelWidth) + (this.args.yLabel ? 32 : 16);
    const layout = computeLayout(width, height, { bottom: 44, left });

    // Value scale: per-category totals when stacked, max single value otherwise.
    const maxVal = this.stacked
      ? Math.max(...labels.map((_, ci) => series.reduce((sum, s) => sum + (s.data[ci] ?? 0), 0)), 0)
      : Math.max(...series.flatMap((s) => s.data.map((v) => v ?? 0)), 0);
    const ticks = niceAxisTicks(0, maxVal);
    const xScale = linearScale(
      [0, ticks[ticks.length - 1]!],
      [layout.left, layout.left + layout.innerWidth]
    );
    const yScale = bandScale(labels, [layout.top, layout.top + layout.innerHeight], 0.25);

    // Vertical gridlines + value-axis tick labels along the bottom.
    const formatFn = this.args.formatValue ?? formatAxisValue;
    ctx.strokeStyle = theme.grid;
    ctx.lineWidth = 1;
    ctx.fillStyle = theme.textMuted;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    for (const tick of ticks) {
      const x = xScale(tick);
      ctx.beginPath();
      ctx.moveTo(x, layout.top);
      ctx.lineTo(x, layout.top + layout.innerHeight);
      ctx.stroke();
      ctx.fillText(formatFn(tick), x, layout.top + layout.innerHeight + 8);
    }

    // Category labels — right-aligned against the plot, vertically centered.
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = theme.textMuted;
    const bw = yScale.bandwidth();
    for (const label of labels) {
      const y = yScale(label) + bw / 2;
      ctx.fillText(truncateToWidth(ctx, label, maxLabelWidth), layout.left - 8, y);
    }

    this.rowRects = [];
    this.segmentRects = [];
    const labelFmt = this.args.formatValue ?? formatNumber;
    const plotRight = layout.left + layout.innerWidth;

    labels.forEach((label, ci) => {
      const rowY = yScale(label);

      if (this.stacked) {
        const capSeriesIdx = lastNonZeroIndex(series, ci);
        let cum = 0;
        series.forEach((s, si) => {
          const v = s.data[ci] ?? 0;
          if (v <= 0) return;
          const xStart = xScale(cum);
          const xEnd = xScale(cum + v);
          ctx.save();
          ctx.fillStyle = this.colorFor(si);
          ctx.globalAlpha = this.segmentAlpha(ci, si);
          fillRectRightRounded(ctx, xStart, rowY, xEnd - xStart, bw, si === capSeriesIdx ? 4 : 0);
          ctx.restore();
          this.segmentRects.push({
            x: xStart,
            w: xEnd - xStart,
            y: rowY,
            h: bw,
            index: ci,
            seriesIndex: si,
          });
          cum += v;
        });
        // One label per stack: the total, just past the bar's end.
        const total = series.reduce((sum, s) => sum + (s.data[ci] ?? 0), 0);
        if (this.showDataLabels && total > 0) {
          this.drawValueLabel(ctx, xScale(total), rowY + bw / 2, labelFmt(total), plotRight, theme);
        }
      } else {
        const subH = bw / Math.max(series.length, 1);
        series.forEach((s, si) => {
          const v = s.data[ci] ?? 0;
          if (v <= 0) return;
          const xEnd = xScale(v);
          ctx.save();
          ctx.fillStyle = this.colorFor(si);
          ctx.globalAlpha = this.segmentAlpha(ci, si);
          fillRectRightRounded(
            ctx,
            xScale(0),
            rowY + si * subH,
            xEnd - xScale(0),
            Math.max(subH - 2, 1),
            3
          );
          ctx.restore();
          this.segmentRects.push({
            x: xScale(0),
            w: xEnd - xScale(0),
            y: rowY + si * subH,
            h: Math.max(subH - 2, 1),
            index: ci,
            seriesIndex: si,
          });
          if (this.showDataLabels) {
            this.drawValueLabel(
              ctx,
              xEnd,
              rowY + si * subH + subH / 2,
              labelFmt(v),
              plotRight,
              theme
            );
          }
        });
      }

      this.rowRects.push({ y: rowY, h: bw, index: ci });
    });

    if (this.args.xLabel) {
      ctx.fillStyle = theme.text;
      ctx.font = '12px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(this.args.xLabel, layout.left + layout.innerWidth / 2, height - 4);
    }
    if (this.args.yLabel) {
      ctx.save();
      ctx.fillStyle = theme.text;
      ctx.font = '12px system-ui, sans-serif';
      ctx.translate(14, layout.top + layout.innerHeight / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.fillText(this.args.yLabel, 0, 0);
      ctx.restore();
    }
  }

  /**
   * Value label at a bar's end: just to the right by default, or inside-right
   * (in white) when the bar reaches the plot edge and the label would overflow.
   * Rows never share a y, so labels can't collide vertically.
   */
  private drawValueLabel(
    ctx: CanvasRenderingContext2D,
    xEnd: number,
    y: number,
    text: string,
    plotRight: number,
    theme: { text: string }
  ) {
    ctx.save();
    ctx.font = '600 10px system-ui, sans-serif';
    ctx.textBaseline = 'middle';
    const w = ctx.measureText(text).width;
    if (xEnd + 4 + w <= plotRight) {
      ctx.fillStyle = theme.text;
      ctx.textAlign = 'left';
      ctx.fillText(text, xEnd + 4, y);
    } else {
      // Bar fills (nearly) the whole axis — label inside its end.
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'right';
      ctx.fillText(text, xEnd - 4, y);
    }
    ctx.restore();
  }

  handleMouseMove(e: MouseEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const y = e.clientY - rect.top;

    const hit = this.rowRects.find((r) => y >= r.y && y <= r.y + r.h);

    if (hit) {
      if (this.hoveredIndex !== hit.index) {
        this.hoveredIndex = hit.index;
        this.draw(canvas);
      }
      this.tooltipX = e.clientX;
      this.tooltipY = rect.top + hit.y - 8;
    } else if (this.hoveredIndex !== null) {
      this.hoveredIndex = null;
      this.draw(canvas);
    }
  }

  /** Reports the segment under the pointer, else the row, to `onBarClick`. */
  handleClick(e: MouseEvent, canvas: HTMLCanvasElement) {
    const onBarClick = this.args.onBarClick;
    if (!onBarClick) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const segment = this.segmentRects.find(
      (s) => x >= s.x && x <= s.x + s.w && y >= s.y && y <= s.y + s.h
    );
    const row = segment ?? this.rowRects.find((r) => y >= r.y && y <= r.y + r.h);
    if (!row) return;
    onBarClick({
      index: row.index,
      seriesIndex: segment?.seriesIndex ?? null,
      clientX: e.clientX,
      clientY: e.clientY,
    });
  }

  handleLegendClick = (index: number) => {
    this.args.onLegendClick?.(index);
  };

  /** Keyboard path for a row: reports it like a click on the row's empty space, anchored at the canvas centre. */
  handleCategoryKey = (index: number, event: Event) => {
    const canvas =
      event.currentTarget instanceof Element
        ? event.currentTarget.parentElement?.closest('div')?.querySelector('canvas')
        : null;
    const rect = canvas?.getBoundingClientRect();
    this.args.onBarClick?.({
      index,
      seriesIndex: null,
      clientX: rect ? rect.left + rect.width / 2 : 0,
      clientY: rect ? rect.top + rect.height / 2 : 0,
    });
  };

  <template>
    <div
      data-test-chart
      data-test-chart-type="horizontal-bars"
      class="relative w-full h-full"
      {{this.installCanvas}}
      ...attributes
    >
      <canvas
        role="img"
        aria-label="Horizontal bar chart"
        class={{if this.canClick "cursor-pointer"}}
        {{style width=(concat this.containerWidth "px") height=(concat this.height "px")}}
      ></canvas>

      {{#if this.canClick}}
        {{! The canvas is pointer-only; these give keyboard and screen-reader users the same per-row action. }}
        <ul class="sr-only" data-test-chart-row-actions>
          {{#each this.labels as |label i|}}
            <li>
              <Button
                @appearance="link"
                @size="sm"
                @testHook="horizontal-bars-chart__row-action"
                {{on "click" (fn this.handleCategoryKey i)}}
              >
                Filter by
                {{label}}
              </Button>
            </li>
          {{/each}}
        </ul>
      {{/if}}

      {{#if this.tooltipRows.length}}
        <ChartTooltip
          @x={{this.tooltipX}}
          @y={{this.tooltipY}}
          class="-translate-x-1/2 -translate-y-full"
          data-test-chart-tooltip
        >
          <div class="font-medium mb-1 tio-text-muted">{{this.tooltipLabel}}</div>
          {{#each this.tooltipRows as |row|}}
            <div class="tio-row-between gap-3">
              <span class="tio-row-extra-tight">
                <span class="tio-chart-dot-swatch" {{style color=row.color}}></span>
                {{row.label}}
              </span>
              <span class="font-medium">{{row.value}}</span>
            </div>
          {{/each}}
          {{#if this.tooltipMoreCount}}
            <div class="mt-1 text-xs tio-text-muted">+ {{this.tooltipMoreCount}} more</div>
          {{/if}}
          {{#if this.tooltipTotal}}
            <div class="tio-row-between gap-3 mt-1 font-medium">
              <span>Total</span>
              <span>{{this.tooltipTotal}}</span>
            </div>
          {{/if}}
        </ChartTooltip>
      {{/if}}

      {{#if this.showLegend}}
        <div class="tio-wrap gap-x-4 gap-y-1 mt-2 justify-center text-xs tio-fg-muted">
          {{#each this.legendItems as |item i|}}
            {{#if this.canClickLegend}}
              <Button
                @appearance="link"
                @size="sm"
                @testHook="horizontal-bars-chart__legend-item"
                class="tio-row-snug"
                data-test-chart-legend-item
                {{on "click" (fn this.handleLegendClick i)}}
              >
                <span class="tio-chart-square-swatch" {{style color=item.color}}></span>
                {{item.label}}
              </Button>
            {{else}}
              <span class="tio-row-snug">
                <span class="tio-chart-square-swatch" {{style color=item.color}}></span>
                {{item.label}}
              </span>
            {{/if}}
          {{/each}}
        </div>
      {{/if}}
    </div>
  </template>
}

/** Index of the last series with a positive value at row `ci`, or -1. */
function lastNonZeroIndex(series: HorizontalBarSeries[], ci: number): number {
  for (let i = series.length - 1; i >= 0; i--) {
    if ((series[i]!.data[ci] ?? 0) > 0) return i;
  }
  return -1;
}

/** Fill a rect whose right corners may be rounded (radius 0 = sharp). */
function fillRectRightRounded(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number
) {
  if (h <= 0 || w <= 0) return;
  const r = Math.min(radius, h / 2, w);
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x, y + h);
  ctx.closePath();
  ctx.fill();
}

/** Truncate text with an ellipsis so it fits `maxWidth` at the current font. */
function truncateToWidth(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxWidth) {
    t = t.slice(0, -1);
  }
  return `${t}…`;
}

export type { HorizontalBarsChartSignature };
